import _ from "lodash";
import queryString from "query-string";
import React, { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryCache, useMutation, QueryStatus, MutationResultPair } from "react-query";
import { useForm, useFormContext, FormProvider } from "react-hook-form";
import { useParams, useHistory } from "react-router-dom";
import {
  Icon,
  Button,
  Segment,
  Header,
  Grid,
  Divider,
  Placeholder,
  ButtonProps,
  Message,
  Ref,
} from "semantic-ui-react";
import { postCitizenPermit, getCitizenPermitsType, getCitizenPermitRenewal } from "../../../../queries/citizen";
import { getPermit, postPermitCheckout, postPermitCheckoutDemoStart } from "../../../../queries/permits";
import identitySelectors from "../../../../selectors/identity";
import networkSelectors from "../../../../selectors/network";
import ConnectedCheckoutForm from "../../../../components/ConnectedCheckoutForm";
import LoginRegisterCard from "../../../../components/LoginRegisterCard";
import { PublicWrapper } from "../../../../components/PublicWrapper";
import { useFeatureFlag } from "../../../../hooks/use-feature-flag";
import { trackCitizenApply } from "../../../../mixpanel/citizen_apply";
import { RenewalPromptModal } from "../../../../features/permits/components/renewals/RenewalPromptModal";
import PermitsForm from "../../../../features/permits/components/Form";
import PlaceholderUntilLoaded from "../../../../components/global_components/PlaceholderUntilLoaded";
import SubmittingPermit from "../../components/SubmittingPermit";
import "./PermitsApply.scss";
import classNames from "classnames";
import { getStripeSupportStatus } from "../../../../queries/payments";
import { PermitsExtensionsAttribute } from "../../types/PermitsExtensionType";
import { usePermitCustomFields } from "../../hooks/usePermitCustomFields";
import { PermitsCustomFieldTypesNames } from "../../../../constants/permits_custom_field_types_names";
import { buildNewPermitContactPayload } from "../../utils/buildPermitsContactPayload";

const PermitsPayment = ({ active, userId, onSuccess, onSkip, fees, setInstructions, firstStepIsPayment }: any) => {
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const { watch } = useFormContext();
  const permitId = watch("id");

  const permitQuery = useQuery(["permits", permitId], getPermit(networkHeaders));

  const permitsTypeId = watch("permitsTypeId");
  const permitsTypeQuery = useQuery(["permitsType", permitsTypeId], getCitizenPermitsType(networkHeaders, userId), {
    enabled: active && permitsTypeId,
  });
  const payInPersonInstructions = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.pay-in-person-instructions"),
    [permitsTypeQuery.data],
  );

  const requirePayOnline = _.get(permitsTypeQuery.data, "data.attributes.require-pay-online");
  const payInPersonAllowed =
    useMemo(() => _.get(permitsTypeQuery.data, "data.attributes.allow-pay-in-person"), [permitsTypeQuery.data]) &&
    !requirePayOnline;

  const stripeSupportQuery = useQuery(["stripe.support.status"], getStripeSupportStatus(networkHeaders));
  const isStripeConfigured = _.get(stripeSupportQuery, "data['supports-stripe']", false);
  const demoModeFeatureFlag = useFeatureFlag("permits.demo_mode");
  const [demoFeatureFlagLoaded, setDemoFeatureFlagLoaded] = useState(false);
  const [demoModeId, setDemoModeId] = useState(0);

  useEffect(() => {
    setInstructions(payInPersonInstructions);
    if (!active) {
      return;
    }
    let total_fee = 0;
    fees.forEach((element: any) => {
      total_fee += element.amount;
    });
    // Skip the payment step, and move to the confirmation step if:
    if (total_fee === 0 || (permitsTypeQuery.data && !firstStepIsPayment)) {
      onSuccess(); //Move to the next step
    }
  }, [active, fees, firstStepIsPayment]);

  useEffect(() => {
    if (demoModeFeatureFlag.status == QueryStatus.Success) {
      if (demoModeFeatureFlag.isEnabled) {
        setDemoModeId(permitId);
      }
      setDemoFeatureFlagLoaded(true);
    } else if (demoModeFeatureFlag.status == QueryStatus.Error) {
      setDemoFeatureFlagLoaded(true);
    }
  }, [active, demoModeFeatureFlag.status]);

  if (!active || !firstStepIsPayment) {
    return <></>;
  }

  if (permitsTypeQuery.isLoading || permitsTypeQuery.isFetching) {
    return (
      <div
        className={classNames({ "tw-h-full tw-pt-5 tw-text-center": true, "tw-block": active, "tw-hidden": !active })}
      >
        <Segment>
          <Placeholder>
            <Placeholder.Line />
            <Placeholder.Line />
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder>
        </Segment>
      </div>
    );
  }

  return (
    <Grid centered>
      <Grid.Row>
        <Grid.Column computer={10} mobile={16}>
          <Segment className='tw-p-4'>
            <Header as='h3'>{"Review charges and pay"}</Header>
            {demoFeatureFlagLoaded && isStripeConfigured && (
              <ConnectedCheckoutForm
                fees={fees}
                checkout={
                  demoModeId > 0
                    ? postPermitCheckoutDemoStart(networkHeaders, permitId)
                    : postPermitCheckout(networkHeaders, permitId)
                }
                onSuccess={async (charges: any) => {
                  onSuccess(charges);
                }}
                demoModeId={demoModeId}
                subtotal={permitQuery.data?.data.attributes.subtotal}
                type='permit-apply'
              />
            )}
            <PayLaterOrInPerson
              payInPersonAllowed={payInPersonAllowed}
              onConfirmPayLater={() => onSkip()}
              onConfirmPayByCashOrCheck={() => onSkip()}
            />
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

interface IPayLaterOrInPerson {
  payInPersonAllowed: boolean;
  onConfirmPayLater: (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
  onConfirmPayByCashOrCheck: (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
}
const PayLaterOrInPerson = ({
  payInPersonAllowed,
  onConfirmPayLater,
  onConfirmPayByCashOrCheck,
}: IPayLaterOrInPerson) => {
  const [displayPayLaterCard, setDisplayPayLaterCard] = useState<boolean>(false);

  const onDisplayPayLaterCard = () => {
    setDisplayPayLaterCard(true);
  };

  if (payInPersonAllowed) {
    return (
      <div className='tw-mt-4 tw-text-center'>
        <Divider horizontal> Or </Divider>
        <Button secondary fluid onClick={onConfirmPayByCashOrCheck}>
          Pay by Cash or Check
        </Button>
      </div>
    );
  }

  return (
    <PayLaterButtonOrCard
      displayPayLaterCard={displayPayLaterCard}
      onDisplayPayLaterCard={onDisplayPayLaterCard}
      onConfirmPayLater={onConfirmPayLater}
    />
  );
};

interface IPayLaterButtonOrCard {
  displayPayLaterCard: boolean;
  onDisplayPayLaterCard: (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
  onConfirmPayLater: (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
}
const PayLaterButtonOrCard = ({
  displayPayLaterCard,
  onDisplayPayLaterCard,
  onConfirmPayLater,
}: IPayLaterButtonOrCard) => {
  if (displayPayLaterCard) {
    return <PayLaterCard onConfirmPayLater={onConfirmPayLater} />;
  }

  return (
    <Button className='button-ghost tw-mt-1' fluid onClick={onDisplayPayLaterCard}>
      Pay Later
    </Button>
  );
};

interface IPayLaterCard {
  onConfirmPayLater: (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
}
const PayLaterCard = ({ onConfirmPayLater }: IPayLaterCard) => {
  const payLaterCardRef = useRef<HTMLElement>(null);
  const focusPayLaterCard = () => {
    payLaterCardRef.current?.focus();
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      focusPayLaterCard();
    }, 100);
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Message fluid>
      <Message.Content>
        <Ref innerRef={payLaterCardRef}>
          <Message.Header tabIndex={-1} innerRef={payLaterCardRef}>
            Pay Later?
          </Message.Header>
        </Ref>
        <p>
          Your application may not be reviewed until a payment has been made. You will be able to pay at anytime by
          viewing this application in <strong>My Dashboard.</strong>
        </p>
        <Button secondary onClick={onConfirmPayLater}>
          Yes, I still want to pay later
        </Button>
      </Message.Content>
    </Message>
  );
};

interface IPermitsSubmit {
  active: boolean;
  onSuccess: Function;
  mutation: MutationResultPair<Response, unknown, any, unknown>;
  permitsFormId: number;
  parentId: string;
}
const PermitsSubmit = ({ active, onSuccess, mutation, permitsFormId, parentId }: IPermitsSubmit) => {
  const { handleSubmit, setError, formState } = useFormContext();
  const { formCustomFieldsMap } = usePermitCustomFields(permitsFormId);

  const { errors, isSubmitted, isSubmitting, isSubmitSuccessful } = formState;
  const [submitPermit] = mutation;

  /**
   * Sanitize permit extension attribute data
   * @param permitsExtensions
   */
  function normalizePermitsExtensionsAttributes(permitsExtensions: PermitsExtensionsAttribute[]) {
    return permitsExtensions.map((extension: PermitsExtensionsAttribute) => {
      const isApplicant = extension.permits_extension_def_id === 0;
      const customField = formCustomFieldsMap[extension.permits_form_field_id];
      if (!isApplicant && !customField) return extension;

      // Reshape address fields for single contact fields
      if (isApplicant || customField.type === PermitsCustomFieldTypesNames.CONTACT_INFORMATION) {
        const newValue = extension.value ? buildNewPermitContactPayload(extension.value) : { "contact-id": null }; // TODO: is it possible to make this null?
        return {
          ...extension,
          value: newValue,
        };
      }

      // Reshape address fields for multiple contact fields and delete non-visible contacts
      if (customField?.type === PermitsCustomFieldTypesNames.ADDITIONAL_CONTACTS) {
        const newValue =
          extension.value?.contacts
            ?.filter((e: { visibility: boolean }) => e.visibility) // visibility=false means the contact was deleted in the form (a little hacky, but works for now)
            .map((e: any) => buildNewPermitContactPayload(e)) ?? [];
        return {
          ...extension,
          value: newValue,
        };
      }

      return extension;
    });
  }

  /**
   * Submit permit application
   * @param data
   */
  async function submitPermitApplication(data: {
    applicant_id: string; // TODO: can we get this to be camelcase
    permitsTypeId: string;
    permits_extensions_attributes: PermitsExtensionsAttribute[];
  }) {
    // Sanitize permit extension attribute data
    const permitsExtensionsAttributes = normalizePermitsExtensionsAttributes(data.permits_extensions_attributes);

    // Parse permit extensions to find applicant_id
    const applicantId = permitsExtensionsAttributes.find((e) => e.value?.isApplicant)?.value.user_id ?? 0;

    const payload = {
      applicant_id: data.applicant_id || applicantId,
      permitsTypeId: data.permitsTypeId,
      permits_extensions_attributes: permitsExtensionsAttributes,
      ...(parentId && { "parent-id": parentId }),
    };

    // Submit permit application
    const response = await submitPermit(payload);
    if (response?.ok) {
      const json = await response.json();
      const firstStep = _.get(json, "data.attributes.steps.0.data.attributes.step-type", "");
      const firstStepIsPayment = firstStep === "payment";
      const confirmationNumber = _.get(json, "data.attributes.confirmation-number");
      const fees = _.get(json, "data.attributes.fee-details", []);
      const id = _.get(json, "data.id");
      onSuccess(id, confirmationNumber, fees, firstStepIsPayment);
    } else {
      const getErrorMessage = async (response: any) => {
        const contentType = response.headers.get("content-type");
        if (contentType?.startsWith("text/plain")) {
          const text = await response.text();
          return text;
        }
        const json = await response.json();
        return _.get(json, "error");
      };
      const errorMessage = await getErrorMessage(response);
      setError("network", {
        type: "manual",
        message: "We were unable to accept your permit application at this time: " + errorMessage,
      });
    }
  }

  // Submit permit application on mount
  useEffect(() => {
    if (!active) return;
    handleSubmit(submitPermitApplication)();
  }, [handleSubmit, active, submitPermit]);

  if (!isSubmitted || isSubmitting) {
    return <SubmittingPermit active={active} />;
  } else if (!isSubmitSuccessful) {
    return (
      <div style={{ textAlign: "center", height: "100%", display: active ? "block" : "none" }}>
        <Segment basic>
          <Icon size='huge' name='exclamation circle' color='red' />
        </Segment>
        <p>{_.get(errors, "network.message")}</p>
      </div>
    );
  } else {
    return <></>;
  }
};

const PermitsApplication = (props: any) => {
  const { active, onSuccess, onCancel, contentRef, isRenew, isStaffOnly, permitId, renewalData } = props;
  const [sectionIndex, setSectionIndex] = useState(0);
  const [complete, setComplete] = useState(false);
  const { watch, clearErrors } = useFormContext();
  const permitsTypeId = watch("permitsTypeId");
  const isLoggedIn = useSelector(identitySelectors.isLoggedIn);
  const history = useHistory();
  const goBack = () => {
    history.goBack();
  };
  const onNext = useCallback(() => {
    if (complete) {
      onSuccess();
      return;
    }
    (contentRef.current as any).scrollIntoView();
    setSectionIndex(sectionIndex + 1);
  }, [sectionIndex, setSectionIndex, complete, onSuccess]);
  const onBack = useCallback(() => {
    if (sectionIndex == 0) {
      goBack();
      return;
    }
    clearErrors();
    setComplete(false);
    setSectionIndex(sectionIndex - 1);
  }, [onCancel, sectionIndex, setSectionIndex]);

  if (!active) {
    return <></>;
  }

  if (!isLoggedIn) {
    return <LoginRegisterCard />;
  }

  return (
    <PermitsForm
      guided
      enforceRequired
      permitsTypeId={permitsTypeId}
      sectionIndex={sectionIndex}
      permitId={permitId}
      onComplete={() => setComplete(true)}
      onNext={onNext}
      onBack={onBack}
      stacked
      isRenew={isRenew}
      isStaffOnly={isStaffOnly}
      renewalData={renewalData}
      isApplyingScreen={true}
    />
  );
};

const PermitsConfirmation = ({
  active,
  confirmationNumber,
  setConfirmation,
  message,
  permitName,
}: {
  active: boolean;
  setConfirmation: any;
  confirmationNumber: string | undefined;
  message: string | undefined;
  permitName: string;
}) => {
  const formattedConfirmationNumber = confirmationNumber?.match(/.{3}/g)?.join("-");
  const history = useHistory();
  const { watch } = useFormContext();
  const permitId = watch("id");
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const permitQuery = useQuery(["permits", permitId], getPermit(networkHeaders), {
    enabled: permitId,
  });
  const isLoading = permitQuery.isLoading;
  const contacts = useMemo(() => _.get(permitQuery.data, "data.attributes.permits-contacts", []), [permitQuery.data]);
  const applicant = contacts?.find((contact: any) => contact["is_applicant"] === true);
  const submitter = contacts?.find((contact: any) => contact["is_submitter"] === true);

  // Render fields based on if its a new or old permit, or if the submitter is the same as the applicant
  const summaryInfo = [
    {
      label: "Applied For",
      value: permitName,
    },
    {
      label: "Applicant Email",
      value: applicant?.email || submitter?.email,
    },
    ...(applicant?.email && submitter?.email !== applicant?.email
      ? [
          {
            label: "Submitter Email",
            value: submitter?.email,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!active) {
      return;
    }
    setConfirmation("Your Application Has Been Submitted!");
  }, [active]);

  return (
    <>
      <div className='permits-confirmation-screen' style={{ display: active ? "flex" : "none" }}>
        <Segment className='main-confirmation-card'>
          <Header as='h2' className='tw-text-2xl tw-font-normal'>
            {`Your Application Number is `}
            <b style={{ color: "#007A6C" }}>{formattedConfirmationNumber}</b>
          </Header>
          {message && <p>{message}</p>}
          <Button size='medium' primary onClick={() => history.push(`details/${permitId}/application`)}>
            View Application
          </Button>
        </Segment>
        <Segment className='app-details'>
          <Header as='h2' className='tw-text-xl' content='Application Details' />
          {summaryInfo.map((info, index) => (
            <PlaceholderUntilLoaded key={index} isLoading={isLoading}>
              <div className='summary-info'>
                <div className='label'>{info.label}</div>
                <div className='value'>{info.value}</div>
              </div>
            </PlaceholderUntilLoaded>
          ))}
        </Segment>
      </div>
    </>
  );
};

export const PermitsApply = ({ location }: any) => {
  const isCitizen = useSelector(identitySelectors.isCitizen);
  const params = queryString.parse(location.search) as any;
  const { site } = useParams<{ site: string }>();
  const [step, setStep] = useState(0);
  const [fees, setFees] = useState<Array<any>>([]);
  const [firstStepIsPayment, setFirstStepIsPayment] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState<string>();
  const [payInPersonInstructions, setPayInPersonInstructions] = useState<string>();
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const [confirmation, setConfirmation] = useState();
  const contentRef = useRef(null);
  const queryCache = useQueryCache();
  const history = useHistory();
  const { userId } = queryString.parse(location.search) as { userId: string };
  const permitsTypeQuery = useQuery(
    ["permitsTypes", params.permitsTypeId],
    getCitizenPermitsType(networkHeaders, userId),
    {
      enabled: params.permitsTypeId,
    },
  );
  const permitsFormId = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.permits-form-id"),
    [permitsTypeQuery.data],
  );
  const renewalPermits = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.renewal_permits", ""),
    [permitsTypeQuery.data],
  );
  const permitName = useMemo(() => _.get(permitsTypeQuery.data, "data.attributes.name", ""), [permitsTypeQuery.data]);
  document.title = `Apply for ${permitName}`;
  const [open, setOpen] = useState(true);
  const beginNewApplication = () => {
    trackCitizenApply(site, permitName);
    history.push(`/${site}/public/permits/apply?permitsTypeId=${params.permitsTypeId}`);
  };
  // useForm initialization
  const permitsApplyForm = useForm({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      id: null,
      permitsTypeId: null,
    },
  });
  const next = useCallback(() => {
    (contentRef.current as any).scrollIntoView({});
    setStep(step + 1);
  }, [step, setStep, contentRef]);
  const { parentId } = queryString.parse(location.search) as { parentId: string };
  const createPermit = useMutation(postCitizenPermit(networkHeaders, userId, parentId), {
    onSuccess: () => queryCache.invalidateQueries("citizenPermits"),
  });

  const isStaffOnly = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.staff-only", ""),
    [permitsTypeQuery.data],
  );

  const parentIdPresent = useMemo(() => {
    const parentId = _.toNumber(params.parentId);
    return _.isNumber(parentId) && parentId > 0;
  }, [params.parentId]);
  const permitRenewalQuery = useQuery(["permitRenewalData"], getCitizenPermitRenewal(networkHeaders, params.parentId), {
    enabled: parentIdPresent,
  });
  const permitQuery = useQuery(["permits", parentId], getPermit(networkHeaders), {
    enabled: parentId,
  });
  useEffect(() => {
    if (permitQuery.isLoading) {
      return;
    }
    if (permitQuery.data?.data?.attributes["renewal-status"] === "active_renewal") {
      window.location.href = `/${site}/public/permits/list?status=active&parentId=${parentId}`;
    } else if (permitQuery.data?.data?.attributes["renewal-status"] === "renewed") {
      window.location.href = `/${site}/public/permits/list?status=renewed&parentId=${parentId}`;
    }
  }, [permitQuery.data]);

  // Apply url parameter
  useEffect(() => {
    const params = queryString.parse(location.search) as any;
    if (params.userId) {
      delete params.userId;
    }
    if (params.staffSubmission) {
      delete params.staffSubmission;
    }
    if (params.permitsTypeId) {
      setStep(1);
      permitsApplyForm.reset({ ...params });
      permitsApplyForm.trigger();
    }
  }, []);

  return (
    <PublicWrapper confirmation={confirmation} contentRef={contentRef} homeUrl={`/${site}/public/permits`}>
      <FormProvider {...permitsApplyForm}>
        <Grid style={{ margin: 0 }} verticalAlign='top' textAlign='center'>
          <Grid.Column style={{ textAlign: "left" }}>
            {renewalPermits.length > 0 && !parentId && userId && (
              <RenewalPromptModal
                userId={userId}
                id={params.permitsTypeId}
                site={site}
                open={open}
                onClose={() => setOpen(false)}
                beginNewApplication={beginNewApplication}
                name={permitName}
                renewalPermits={renewalPermits}
              />
            )}
            <input type='hidden' {...(permitsApplyForm.register("id") as any)} />
            <PermitsApplication
              active={step == 1}
              permitId={parentId}
              contentRef={contentRef}
              onCancel={() => setStep(0)}
              onSuccess={next}
              isRenew={!!parentId}
              isStaffOnly={isStaffOnly}
              renewalData={permitRenewalQuery?.data?.data}
            />
            <PermitsSubmit
              active={step == 2}
              parentId={parentId}
              onSuccess={(id: string, confirmationNumber: string, fees: any[], firstStepIsPayment: boolean) => {
                permitsApplyForm.setValue("id", id);
                setConfirmationNumber(confirmationNumber);
                setFees(fees);
                setFirstStepIsPayment(firstStepIsPayment);
                if (!isCitizen && !!params.userId) {
                  history.push(`/${site}/permits/review/${id}#details`);
                } else {
                  next();
                }
              }}
              mutation={createPermit}
              permitsFormId={permitsFormId}
            />
            <PermitsPayment
              fees={fees}
              active={step == 3}
              userId={userId}
              onSuccess={next}
              setInstructions={(payInPersonInstructions: string) => {
                setPayInPersonInstructions(payInPersonInstructions);
              }}
              onSkip={next}
              firstStepIsPayment={firstStepIsPayment}
            />
            <PermitsConfirmation
              active={step == 4}
              confirmationNumber={confirmationNumber}
              setConfirmation={setConfirmation}
              message={payInPersonInstructions}
              permitName={permitName}
            />
          </Grid.Column>
        </Grid>
      </FormProvider>
    </PublicWrapper>
  );
};

export default PermitsApply;
